export type RtcStatsDirection = 'outbound' | 'inbound';

export interface RtcStatsRecord {
  id: string;
  type: string;
  timestamp: number;
  kind?: string;
  mediaType?: string;
  isRemote?: boolean;
  bytesSent?: number;
  bytesReceived?: number;
  framesEncoded?: number;
  framesDecoded?: number;
  frameWidth?: number;
  frameHeight?: number;
  mediaSourceId?: string;
  width?: number;
  height?: number;
}

export interface RtcStatsCollection {
  forEach(callback: (value: RtcStatsRecord, key: string) => void): void;
  get?(key: string): RtcStatsRecord | undefined;
}

export interface RtcStreamMeasurement {
  direction: RtcStatsDirection;
  kilobitsPerSecond: number;
  framesPerSecond: number;
  width: number;
  height: number;
  sampledAtMs: number;
}

interface CounterSnapshot {
  id: string;
  timestamp: number;
  bytes: number;
  frames: number;
}

function finiteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function videoRtp(report: RtcStatsCollection, direction: RtcStatsDirection): RtcStatsRecord | undefined {
  let selected: RtcStatsRecord | undefined;
  report.forEach((entry) => {
    if (entry.type !== `${direction}-rtp` || entry.isRemote) return;
    if (entry.kind !== 'video' && entry.mediaType !== 'video') return;
    const bytes = direction === 'outbound' ? entry.bytesSent : entry.bytesReceived;
    const frames = direction === 'outbound' ? entry.framesEncoded : entry.framesDecoded;
    if (!finiteNonNegative(entry.timestamp) || !finiteNonNegative(bytes) || !finiteNonNegative(frames)) return;
    const selectedBytes = direction === 'outbound' ? selected?.bytesSent : selected?.bytesReceived;
    if (!selected || !finiteNonNegative(selectedBytes) || bytes > selectedBytes) selected = entry;
  });
  return selected;
}

/** Stateful delta sampler for one RTP direction. The first report is only a baseline. */
export class RtcStatsSampler {
  readonly #direction: RtcStatsDirection;
  #previous?: CounterSnapshot;

  constructor(direction: RtcStatsDirection) {
    this.#direction = direction;
  }

  reset(): void {
    this.#previous = undefined;
  }

  add(report: RtcStatsCollection): RtcStreamMeasurement | undefined {
    const rtp = videoRtp(report, this.#direction);
    if (!rtp) {
      this.reset();
      return undefined;
    }
    const bytes = this.#direction === 'outbound' ? rtp.bytesSent : rtp.bytesReceived;
    const frames = this.#direction === 'outbound' ? rtp.framesEncoded : rtp.framesDecoded;
    if (!finiteNonNegative(rtp.timestamp) || !finiteNonNegative(bytes) || !finiteNonNegative(frames)) {
      this.reset();
      return undefined;
    }

    const current = { id: rtp.id, timestamp: rtp.timestamp, bytes, frames };
    const previous = this.#previous;
    this.#previous = current;
    if (!previous || previous.id !== current.id) return undefined;
    const elapsedMs = current.timestamp - previous.timestamp;
    const byteDelta = current.bytes - previous.bytes;
    const frameDelta = current.frames - previous.frames;
    if (elapsedMs <= 0 || byteDelta < 0 || frameDelta < 0) return undefined;

    const mediaSource = rtp.mediaSourceId ? report.get?.(rtp.mediaSourceId) : undefined;
    const width = rtp.frameWidth ?? mediaSource?.width ?? mediaSource?.frameWidth;
    const height = rtp.frameHeight ?? mediaSource?.height ?? mediaSource?.frameHeight;
    if (!finiteNonNegative(width) || width === 0 || !finiteNonNegative(height) || height === 0) return undefined;

    return {
      direction: this.#direction,
      // bits/ms is numerically decimal kbit/s (1 kbit = 1000 bits).
      kilobitsPerSecond: (byteDelta * 8) / elapsedMs,
      framesPerSecond: (frameDelta * 1_000) / elapsedMs,
      width,
      height,
      sampledAtMs: current.timestamp,
    };
  }
}
